/**
 * 🚨 Give every node in a built tree a unique, content-sensitive identity.
 *
 * ## The bug this exists to fix
 *
 * `@tiendanube/nube-sdk-ui` derives each component's `__internalId` from a hash of its props
 * (`generateInternalId` → `djb2Hash(stableStringify(props))`). Two details of that serializer make
 * distinct components collide:
 *
 * ```js
 * if (key === "children" && typeof val !== "string") return "[children]";  // arrays are dropped
 * if (typeof val === "function") return `[${val.toString()}]`;             // SOURCE text, not identity
 * ```
 *
 * Every component we build passes `children` as an **array**, so children never contribute to the
 * hash. And two closures written at the same source location — `() => select(r.id)` for rate A and
 * rate B — stringify identically. Style objects come from one shared `codStyles()` result, so they
 * serialise identically too.
 *
 * Net effect, all observed on a live store:
 *
 * | Symptom | Cause |
 * | --- | --- |
 * | "the price doesn't update in the summary" | a totals row's only change is its text, so its id is unchanged and the host skips the update |
 * | "I can't click free shipping again" | both rate cards hash to ONE id, so both clicks reach the last-registered handler |
 * | "it adds a duplicated record" | same collision, resolved the other way by the host |
 * | the quantity stepper going dead | `−` and `+` differ only in their (array) children |
 *
 * ## The fix
 *
 * One post-build pass that gives every node an id derived from **its path in the tree plus its own
 * direct text**, appended to whatever the SDK generated.
 *
 *  - *Path* makes siblings distinct, which fixes the handler collisions.
 *  - *Own text* makes a node whose caption changed a different node, which fixes the stale amounts.
 *  - **Only DIRECT string children** count. Hashing a whole subtree would change an ancestor's id
 *    whenever anything under it changed, remounting the focused input on every repaint — the very
 *    thing that broke typing.
 *
 * Both inputs are deterministic, so an unchanged node keeps its id across renders and the host can
 * still reuse its DOM node.
 *
 * Appending (rather than replacing) keeps the SDK's own format — `${type}-${appId}-${hash}`, which
 * `isValidInternalId` matches as `[a-z0-9]+` in the last segment — so a base36 suffix stays valid
 * and the props that fed the original hash still matter.
 *
 * ## Explicit keys
 *
 * A node may set `key` to name itself; that name is used as its path segment instead of its index.
 * Worth doing wherever identity should survive a sibling appearing or disappearing above it — a
 * field (`field-phone`), a rate card (`rate-abc`). Everything else is positional, exactly like
 * React's index keys.
 */
/** djb2 in base36 — the same hash the SDK uses, so the suffix matches its id grammar. */
function djb2(input) {
    let hash = 5381;
    for (let i = 0; i < input.length; i++)
        hash = (hash * 33) ^ input.charCodeAt(i);
    return (hash >>> 0).toString(36);
}
function childList(children) {
    if (children === undefined || children === null)
        return [];
    return Array.isArray(children) ? children : [children];
}
function walk(node, path) {
    if (node === null || typeof node !== 'object')
        return;
    const n = node;
    const kids = childList(n.children);
    // Only this node's OWN text — see the header on why the subtree is deliberately excluded.
    const ownText = kids.filter((c) => typeof c === 'string').join(' ');
    if (typeof n.__internalId === 'string')
        n.__internalId += djb2(`${path}|${ownText}`);
    // Read the author's key BEFORE overwriting it, so explicit keys still name their children below.
    const segments = kids.map((c, i) => {
        const key = c?.key;
        return key === undefined || key === null ? String(i) : String(key);
    });
    n.key = path;
    kids.forEach((child, i) => {
        if (typeof child === 'string')
            return;
        walk(child, `${path}.${segments[i] ?? String(i)}`);
    });
}
/**
 * Stamp a built tree with unique identities, in place, and return it.
 *
 * Called once at the end of `buildCodForm` — and by any other consumer that hands a tree to
 * `nube.render()`, such as the open button, which is built outside the form.
 */
export function withIdentity(root, prefix = 'r') {
    walk(root, prefix);
    return root;
}
//# sourceMappingURL=identity.js.map